import React from 'react';

import { Icon } from 'client/shared/Icon';
import { IconBadge } from 'client/components/IconBadge';
import { useT } from 'client/i18n/hooks/useT';
import { MarketingButton } from 'client/components/MarketingPagesShared/MarketingButton';
import { MarketingAppButtons } from 'client/components/MarketingPagesShared/MarketingAppButtons';
import { SECONDARY_MOBILE_LINKS } from '../../constants';
import { clickedNavLinkGoogleAnalytics } from '../../events';
import { NavLinkProps } from '../../types';
import {
  MarketingAppButtonsContainer,
  BadgeContainer,
  PrimaryLink,
  AnchorWrapper,
  SecondaryLink,
  Nav,
  Content,
  MobileNavContainer,
  MobileNavContentWrapper,
  MobileNavTopSection,
  StyledIconLink,
  MobileNavClosedButton,
  MobileNavCloseLine,
  MobileNavButtonContainer,
} from './styles';

type MobileNavProps = {
  links: NavLinkProps[];
  isSignedIn: boolean;
  onLogOut: () => void;
  onClickClose: () => void;
  pathname: string;
};

export const MobileNav = ({
  links,
  isSignedIn,
  onLogOut,
  onClickClose,
  pathname,
}: MobileNavProps) => {
  const t = useT();
  const translatedMobileNavLinks = {
    ads: t(
      'PublicWebsiteNavFooter.Ads',
      'Ads by Anchor',
      'Link to "Ads by Anchor" web page'
    ),
    blog: t('PublicWebsiteNavFooter.Blog', 'Blog', 'Link to "Blog" web page'),
    careers: t(
      'PublicWebsiteNavFooter.Careers',
      'Careers',
      'Link to "Careers" web page'
    ),
    dashboard: t(
      'PublicWebsiteNavFooter.Dashboard',
      'Dashboard',
      'Link to "Dashboard" web page'
    ),
    episodes: t(
      'PublicWebsiteNavFooter.Episodes',
      'Episodes',
      'Link to "Episodes" web page'
    ),
    features: t(
      'PublicWebsiteNavFooter.Features',
      'Features',
      'Link to "Features" web page'
    ),
    help: t('PublicWebsiteNavFooter.Help', 'Help', 'Link to "Help" web page'),
    money: t(
      'PublicWebsiteNavFooter.Money',
      'Money',
      'Link to "Money" web page'
    ),
    network: t(
      'PublicWebsiteNavFooter.Network',
      'Network',
      'Link to "Network" web page'
    ),
    switch: t(
      'PublicWebsiteNavFooter.SwitchToAnchor',
      'Switch to Anchor',
      'Link to "Switch to Anchor" web page'
    ),
  } as { [key: string]: any };

  const SIGNED_IN_MOBILE_LINKS = [
    {
      label: t(
        'PublicWebsiteNavFooter.UpdateSettings',
        'Update settings',
        'Link to "Update settings" web page'
      ),
      to: '/dashboard/podcast/edit',
    },
    {
      label: t(
        'DistributionMenuItem.PodcastAvailability',
        'Podcast Availability',
        'Link to see "Podcast Availability"'
      ),
      to: '/dashboard/podcast/distribution',
    },
  ];

  return (
    <MobileNavContainer>
      <MobileNavContentWrapper>
        <MobileNavTopSection>
          <StyledIconLink
            href=""
            className=""
            isDownloadLink=""
            to={isSignedIn ? '/dashboard' : '/'}
            aria-label={t(
              'PublicWebsiteNavFooter.Home.AriaLabel',
              'Anchor logo that navigates back to Home',
              'Anchor logo that navigates back to Home'
            )}
            onClick={() => {
              onClickClose();
              clickedNavLinkGoogleAnalytics('Home');
            }}
          >
            <Icon type="anchor_logo" fillColor="white" />
          </StyledIconLink>
          <MobileNavClosedButton
            on="tap:sidebar.close"
            type="button"
            tabIndex={0}
            onClick={onClickClose}
            aria-label="Close navigation menu"
          >
            <MobileNavCloseLine className="close-line-one" />
            <MobileNavCloseLine className="close-line-two" />
          </MobileNavClosedButton>
        </MobileNavTopSection>
        <Content>
          <Nav>
            {links.map(
              ({
                label,
                href,
                to,
                key,
                openInNewTab,
                isShowingNotificationBadge,
                notificationBadge,
              }) => (
                <PrimaryLink
                  className=""
                  isDownloadLink={false}
                  key={label}
                  isActive={pathname === (to || href)}
                  onClick={() => {
                    onClickClose();
                    clickedNavLinkGoogleAnalytics(label);
                  }}
                  href={href}
                  to={to}
                  target={openInNewTab ? '_blank' : undefined}
                >
                  <AnchorWrapper>
                    {translatedMobileNavLinks[key]}
                    {isShowingNotificationBadge && (
                      <BadgeContainer>
                        <IconBadge
                          backgroundColor="green"
                          iconColor="#24203F"
                          width={10}
                          type={notificationBadge}
                        />
                      </BadgeContainer>
                    )}
                  </AnchorWrapper>
                </PrimaryLink>
              )
            )}
            {isSignedIn &&
              SIGNED_IN_MOBILE_LINKS.map(({ label, to }) => (
                <SecondaryLink
                  to={to}
                  key={label}
                  href={undefined}
                  onClick={onClickClose}
                  target={undefined}
                  isActive={pathname === to}
                  isDownloadLink={false}
                  className=""
                >
                  {label}
                </SecondaryLink>
              ))}

            {(SECONDARY_MOBILE_LINKS || []).map(
              ({ label, key, href, target }) => (
                <SecondaryLink
                  key={label}
                  isActive={pathname === href}
                  onClick={() => {
                    onClickClose();
                    clickedNavLinkGoogleAnalytics(label);
                  }}
                  target={target}
                  href={href}
                  to={undefined}
                  isDownloadLink={false}
                  className=""
                >
                  {translatedMobileNavLinks[key]}
                </SecondaryLink>
              )
            )}
          </Nav>
        </Content>
        {isSignedIn ? (
          <MobileNavButtonContainer>
            <MarketingButton kind="button" color="onDark" onClick={onLogOut}>
              {t(
                'PublicWebsiteNavFooter.Mobile.SignOut',
                'Sign out',
                'Sign out button text'
              )}
            </MarketingButton>
          </MobileNavButtonContainer>
        ) : (
          <MobileNavButtonContainer>
            <MarketingAppButtonsContainer>
              <MarketingAppButtons
                clickEventLocation="Menu"
                isRenderForMobileOnly={true}
              />
            </MarketingAppButtonsContainer>
            <MarketingButton
              kind="link"
              href="/signup"
              onClick={() => {
                onClickClose();
                clickedNavLinkGoogleAnalytics('Sign Up');
              }}
            >
              {t(
                'PublicWebsiteNavFooter.Mobile.SignUp',
                'Sign up',
                'Sign up button text'
              )}
            </MarketingButton>

            <MarketingButton
              kind="link"
              href="/login"
              color="onDark"
              onClick={() => {
                onClickClose();
                clickedNavLinkGoogleAnalytics('Log In');
              }}
            >
              {t(
                'PublicWebsiteNavFooter.Mobile.SignIn',
                'Sign in',
                'Sign in button text'
              )}
            </MarketingButton>
          </MobileNavButtonContainer>
        )}
      </MobileNavContentWrapper>
    </MobileNavContainer>
  );
};
